import { useState } from 'react';

import CheckmarkCircle from '@crehana/compass.icons.fill/CheckmarkCircle';
import CloseCircle from '@crehana/compass.icons.fill/CloseCircle';
import AlertTriangle from '@crehana/compass.icons.outline/AlertTriangle';
import CloseSmall from '@crehana/compass.icons.outline/CloseSmall';
import { Popper, PopperContent, PopperTrigger } from '@crehana/compass.ui/v2';
import { clsx } from 'clsx';

type TPopperNotification = {
  onClick?: () => void;
  buttonLabel?: string;
  content?: string;
  title?: string;
  triggerLabel?: string;
};

interface TNotification {
  label: string;
  popper?: TPopperNotification;
  type?: 'default' | 'alert' | 'success';
  onClick?: () => void;
}

const Notification: React.FC<TNotification> = ({
  label,
  popper,
  type = 'default',
  onClick,
}) => {
  const isAlert = type === 'alert';
  const isSuccess = type === 'success';
  const isDefault = type === 'default';
  const [open, setOpen] = useState(false);

  const Icon = isAlert ? CloseCircle : CheckmarkCircle;

  return (
    <div
      className={clsx(
        'tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-1.2 tw-border tw-border-solid tw-rounded-0.8 tw-p-1.6',
        isAlert && 'tw-border-alert-400  tw-bg-alert-100',
        isSuccess && 'tw-border-primary-light-500 tw-bg-primary-light-200',
        isDefault && 'tw-border-neutral-light-500 tw-bg-neutral-light-200',
      )}
    >
      <div className="tw-flex tw-items-center tw-gap-1.2">
        <Icon
          className={clsx(
            isAlert && 'tw-text-alert-400',
            isSuccess && 'tw-text-primary-light-500',
            isDefault && 'tw-text-neutral-light-500',
          )}
          size={16}
        />
        <p className="tw-font-body4">{label}</p>
      </div>
      {popper && isAlert && onClick && (
        <button
          className="tw-text-link-s tw-text-alert-400 hover:tw-text-alert-300"
          type="button"
          onClick={onClick}
        >
          {popper?.triggerLabel || 'Ver detalle de error'}
        </button>
      )}
      {popper && isAlert && !onClick && (
        <Popper open={open}>
          <PopperTrigger
            onClick={() => setOpen(!open)}
            className="tw-text-link-s tw-text-alert-400 hover:tw-text-alert-300"
          >
            {popper?.triggerLabel || 'Ver detalle de error'}
          </PopperTrigger>
          <PopperContent removePadding onBlurCapture={() => setOpen(false)}>
            <div className="tw-relative tw-flex tw-flex-col tw-gap-1.2 tw-py-2 tw-px-2.4 tw-w-28">
              <div className="tw-flex tw-justify-between tw-items-center">
                <AlertTriangle className="tw-text-alert-400" size={20} />
                <button onClick={() => setOpen(false)} type="button">
                  <CloseSmall
                    className="tw-text-neutral-light-500 tw-absolute tw-right-1 tw-top-1"
                    size={24}
                  />
                </button>
              </div>
              <p className="tw-font-subtitle4">{popper?.title}</p>
              <p className="tw-font-body5">{popper?.content}</p>
              <button
                className="tw-text-link-s"
                onClick={popper?.onClick}
                type="button"
              >
                {popper?.buttonLabel}
              </button>
            </div>
          </PopperContent>
        </Popper>
      )}
    </div>
  );
};

export default Notification;
